import { useCallback, useEffect, useRef } from "react"
import { matchPath, useHistory, useLocation } from "react-router-dom"
import { GlobalMachineContext } from "../../../study-plans/pages/StudyPlansInit/StudyPlansInit"
import { EocRouteParams } from "../../../study-plans/types"
import { RouteParams } from "../../../../reporting/types"
import { REPORTING_ROUTE_MATCH } from "../../../../reporting/constants"
import { useFilterStateContext } from "../../../../reporting/providers/FiltersStateProvider"
import { Location } from "history"

export const useStateSetup = () => {
  const history = useHistory()
  const location = useLocation()
  const { send } = GlobalMachineContext.useActorRef()
  const { isOldNav, redirectUrl } = GlobalMachineContext.useSelector(state => ({
    isOldNav: state.context.isOldNav,
    redirectUrl: state.context.redirectUrl
  }))

  // Track the previous location to detect external navigation
  const prevLocationRef = useRef(location.pathname)

  // For new nav, get filters
  const { state: filtersState } = useFilterStateContext() ?? {}

  const updateStateFromURL = useCallback(
    (location: Location) => {
      if (isOldNav) {
        // Parse from URL params for old navigation
        const match = matchPath<EocRouteParams>(location.pathname, {
          path: "/eoc/:program/study-plans/:subject?/:subpage?/:planType?"
        })

        if (!match) return

        const { program, subject, subpage, planType } = match.params

        // Parse query params for classId/className
        const searchParams = new URLSearchParams(location.search)
        const classId = searchParams.get("classId")
        const className = searchParams.get("className")

        send({
          type: "SET_STATE",
          program,
          subject,
          subpage,
          entityType: planType,
          ...(classId && { classId }),
          ...(className && { className })
        })
      } else {
        // Parse from filters state and URL for new navigation
        const [filterProgram, filterSubject] =
          filtersState?.test?.[0]?.split("_") ?? []

        const match = matchPath<RouteParams>(location.pathname, {
          path: REPORTING_ROUTE_MATCH
        })

        if (!match) return

        const { subpage, entityType } = match.params

        // Parse query params
        const searchParams = new URLSearchParams(location.search)
        const classId = searchParams.get("classId")
        const className = searchParams.get("className")

        // For new nav, also check for entity filter
        const entityFilter = filtersState?.entity?.[0]
        let scope = undefined
        if (entityFilter) {
          // Determine scope from entity filter type
          if (entityFilter.startsWith("DISTRICT:")) {
            scope = "DISTRICT"
          } else if (entityFilter.startsWith("SCHOOL:")) {
            scope = "SCHOOL"
          } else if (entityFilter.startsWith("CLASS:")) {
            scope = "CLASS"
          }
        }

        send({
          type: "SET_STATE",
          program: filterProgram,
          subject: filterSubject,
          subpage,
          entityType,
          scope,
          ...(classId && { classId }),
          ...(className && { className })
        })
      }
    },
    [isOldNav, filtersState]
  )

  // Detect external navigation (nav link clicks or direct url entered)
  useEffect(() => {
    // Check if location changed and it's not from xState's redirectUrl
    if (location.pathname !== prevLocationRef.current) {
      // If redirectUrl exists and matches the new location, this is internal navigation
      const isInternalNavigation =
        redirectUrl && redirectUrl.path === location.pathname

      if (!isInternalNavigation) {
        // This is external navigation (nav link click)
        updateStateFromURL(location)
      }

      prevLocationRef.current = location.pathname
    }

    // Listen for browser back/forward
    const unregister = history.listen((location, action) => {
      if (action === "POP") {
        updateStateFromURL(location)
      }
    })

    return () => unregister()
  }, [history, location, redirectUrl])
}
